import {
  EncryptedPDFError,
  PDFArray,
  PDFBool,
  PDFDict,
  PDFDocument,
  PDFHexString,
  PDFName,
  PDFNumber,
  PDFString,
} from 'pdf-lib';

const STANDARD_INFO_KEYS = new Set([
  'Title',
  'Author',
  'Subject',
  'Keywords',
  'Creator',
  'Producer',
  'CreationDate',
  'ModDate',
]);

const NS = {
  dc: 'http://purl.org/dc/elements/1.1/',
  pdf: 'http://ns.adobe.com/pdf/1.3/',
  rdf: 'http://www.w3.org/1999/02/22-rdf-syntax-ns#',
  xmp: 'http://ns.adobe.com/xap/1.0/',
};

export const emptyPdfMetadata = () => ({
  title: '',
  author: '',
  subject: '',
  keywords: '',
  creator: '',
  producer: '',
  created: '',
  modified: '',
  customInfo: [],
});

const toInputDate = (value) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 16);
};

const toPdfDate = (value) => {
  if (!value) return null;
  const localInput = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value);
  const date = localInput
    ? new Date(
        Date.UTC(
          Number(localInput[1]),
          Number(localInput[2]) - 1,
          Number(localInput[3]),
          Number(localInput[4]),
          Number(localInput[5]),
          Number(localInput[6] || 0)
        )
      )
    : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const splitKeywords = (value) =>
  value
    .split(/[;,]/)
    .map((keyword) => keyword.trim())
    .filter(Boolean);

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const matchesAsciiAt = (bytes, text, offset) => {
  for (let index = 0; index < text.length; index += 1) {
    if (bytes[offset + index] !== text.charCodeAt(index)) return false;
  }
  return true;
};

const findAscii = (bytes, text, start = 0) => {
  for (let offset = start; offset <= bytes.length - text.length; offset += 1) {
    if (matchesAsciiAt(bytes, text, offset)) return offset;
  }
  return -1;
};

export const hasPdfSignature = (bytes) =>
  findAscii(bytes, '/ByteRange') >= 0 &&
  (findAscii(bytes, '/Type/Sig') >= 0 || findAscii(bytes, '/Type /Sig') >= 0);

export const hasPdfHeader = (bytes) =>
  bytes.length >= 5 && matchesAsciiAt(bytes, '%PDF-', 0);

const decodeXmlBytes = (bytes) => {
  if (bytes.length >= 2 && bytes[0] === 0xfe && bytes[1] === 0xff) {
    const swapped = new Uint8Array(bytes.length - 2);
    for (let index = 2; index + 1 < bytes.length; index += 2) {
      swapped[index - 2] = bytes[index + 1];
      swapped[index - 1] = bytes[index];
    }
    return new TextDecoder('utf-16le').decode(swapped);
  }
  return new TextDecoder('utf-8').decode(bytes);
};

const extractXmpFromBytes = (bytes) => {
  const start = findAscii(bytes, '<?xpacket');
  if (start < 0) return '';
  const endStart = findAscii(bytes, '<?xpacket end=', start + 9);
  if (endStart < 0) return '';
  const end = findAscii(bytes, '?>', endStart);
  if (end < 0) return '';
  return decodeXmlBytes(bytes.slice(start, end + 2));
};

const extractXmpFromDocument = (pdfDoc) => {
  try {
    const ref = pdfDoc.catalog.get(PDFName.of('Metadata'));
    const stream = ref ? pdfDoc.context.lookup(ref) : null;
    if (!stream || typeof stream.getContents !== 'function') return '';
    const value = decodeXmlBytes(stream.getContents());
    return value.includes('<x:xmpmeta') || value.includes('<xmpmeta') ? value : '';
  } catch {
    return '';
  }
};

const getElement = (doc, namespace, localName) =>
  doc.getElementsByTagNameNS(namespace, localName)[0] || null;

const getContainerText = (element) => {
  if (!element) return '';
  const items = Array.from(element.getElementsByTagNameNS(NS.rdf, 'li'))
    .map((item) => item.textContent.trim())
    .filter(Boolean);
  return items.length ? items.join(', ') : element.textContent.trim();
};

const parseXmp = (xml) => {
  if (!xml) return { values: {}, advanced: [] };
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  if (doc.getElementsByTagName('parsererror').length) {
    return { values: {}, advanced: [] };
  }

  const values = {
    title: getContainerText(getElement(doc, NS.dc, 'title')),
    author: getContainerText(getElement(doc, NS.dc, 'creator')),
    subject: getContainerText(getElement(doc, NS.dc, 'description')),
    keywords:
      getElement(doc, NS.pdf, 'Keywords')?.textContent.trim() ||
      getContainerText(getElement(doc, NS.dc, 'subject')),
    creator: getElement(doc, NS.xmp, 'CreatorTool')?.textContent.trim() || '',
    producer: getElement(doc, NS.pdf, 'Producer')?.textContent.trim() || '',
    created: toInputDate(getElement(doc, NS.xmp, 'CreateDate')?.textContent.trim()),
    modified: toInputDate(getElement(doc, NS.xmp, 'ModifyDate')?.textContent.trim()),
  };

  const standardNames = new Set([
    'dc:title',
    'dc:creator',
    'dc:description',
    'dc:subject',
    'pdf:Keywords',
    'pdf:Producer',
    'xmp:CreatorTool',
    'xmp:CreateDate',
    'xmp:ModifyDate',
  ]);
  const advanced = [];
  const seen = new Set();

  Array.from(doc.getElementsByTagName('*')).forEach((element) => {
    const hasElementChildren = Array.from(element.childNodes).some(
      (node) => node.nodeType === 1
    );
    const value = element.textContent.trim();
    if (!hasElementChildren && value && !standardNames.has(element.nodeName)) {
      const key = element.nodeName;
      const identity = `${key}:${value}`;
      if (!seen.has(identity)) {
        seen.add(identity);
        advanced.push({ key, value, source: 'XMP', editable: false });
      }
    }
  });

  return { values, advanced };
};

const pdfObjectToDisplayValue = (object, context) => {
  const resolved = context.lookup(object);
  if (resolved instanceof PDFString || resolved instanceof PDFHexString) {
    return { value: resolved.decodeText(), editable: true };
  }
  if (resolved instanceof PDFNumber) {
    return { value: String(resolved.asNumber()), editable: false };
  }
  if (resolved instanceof PDFBool) {
    return { value: String(resolved.asBoolean()), editable: false };
  }
  if (resolved instanceof PDFName) {
    return { value: resolved.asString(), editable: false };
  }
  if (resolved instanceof PDFArray) {
    const values = [];
    for (let index = 0; index < resolved.size(); index += 1) {
      values.push(pdfObjectToDisplayValue(resolved.get(index), context).value);
    }
    return { value: values.join(', '), editable: false };
  }
  return { value: resolved?.toString?.() || '', editable: false };
};

const getInfoDictionary = (pdfDoc) => {
  const info = pdfDoc.context.trailerInfo.Info;
  const dictionary = info ? pdfDoc.context.lookup(info) : null;
  return dictionary instanceof PDFDict ? dictionary : null;
};

const readCustomInfo = (pdfDoc) => {
  const dictionary = getInfoDictionary(pdfDoc);
  if (!dictionary) return [];

  return dictionary
    .entries()
    .map(([key, object]) => {
      const name = key.asString();
      if (STANDARD_INFO_KEYS.has(name)) return null;
      const display = pdfObjectToDisplayValue(object, pdfDoc.context);
      return { key: name, ...display, source: 'Info' };
    })
    .filter(Boolean);
};

const mergeMetadata = (infoValues, xmpValues) => {
  const merged = emptyPdfMetadata();
  Object.keys(xmpValues).forEach((key) => {
    if (xmpValues[key]) merged[key] = xmpValues[key];
  });
  Object.keys(infoValues).forEach((key) => {
    if (infoValues[key]) merged[key] = infoValues[key];
  });
  return merged;
};

const friendlyPdfError = (error) => {
  if (error instanceof EncryptedPDFError || /encrypted/i.test(error.message || '')) {
    return new Error('此 PDF 已加密或受密码保护，当前无法安全修改其元数据。');
  }
  return new Error('无法读取此 PDF。请确认文件有效且未损坏。');
};

export async function readPdfBytes(bytes, fileSize = bytes.length) {
  if (!hasPdfHeader(bytes)) {
    throw new Error('所选文件没有有效的 PDF 文件头。');
  }

  let pdfDoc;
  try {
    pdfDoc = await PDFDocument.load(bytes, { updateMetadata: false });
  } catch (error) {
    throw friendlyPdfError(error);
  }

  const xmpXml = extractXmpFromDocument(pdfDoc) || extractXmpFromBytes(bytes);
  const xmp = parseXmp(xmpXml);
  const infoValues = {
    title: pdfDoc.getTitle() || '',
    author: pdfDoc.getAuthor() || '',
    subject: pdfDoc.getSubject() || '',
    keywords: pdfDoc.getKeywords() || '',
    creator: pdfDoc.getCreator() || '',
    producer: pdfDoc.getProducer() || '',
    created: toInputDate(pdfDoc.getCreationDate()),
    modified: toInputDate(pdfDoc.getModificationDate()),
  };
  const metadata = mergeMetadata(infoValues, xmp.values);
  metadata.customInfo = [...readCustomInfo(pdfDoc), ...xmp.advanced];
  const signed = hasPdfSignature(bytes);

  return {
    metadata,
    source: { kind: 'pdf', bytes, xmpXml, signed },
    technical: {
      pageCount: pdfDoc.getPageCount(),
      fileSize: formatFileSize(fileSize),
      encrypted: false,
      signed,
      advancedCount: metadata.customInfo.length,
    },
  };
}

export async function readPdfMetadata(file) {
  const bytes = new Uint8Array(await file.arrayBuffer());
  return readPdfBytes(bytes, file.size);
}

const setTextMetadata = (pdfDoc, key, value) => {
  const setters = {
    Title: () => pdfDoc.setTitle(value),
    Author: () => pdfDoc.setAuthor(value),
    Subject: () => pdfDoc.setSubject(value),
    Creator: () => pdfDoc.setCreator(value),
    Producer: () => pdfDoc.setProducer(value),
  };
  if (value) setters[key]();
};

const deleteEmptyStandardValues = (pdfDoc, metadata) => {
  const dictionary = getInfoDictionary(pdfDoc);
  if (!dictionary) return;
  const values = {
    Title: metadata.title,
    Author: metadata.author,
    Subject: metadata.subject,
    Keywords: metadata.keywords,
    Creator: metadata.creator,
    Producer: metadata.producer,
    CreationDate: metadata.created,
    ModDate: metadata.modified,
  };
  Object.entries(values).forEach(([key, value]) => {
    if (!value) dictionary.delete(PDFName.of(key));
  });
};

const updateCustomInfo = (pdfDoc, entries) => {
  const dictionary = getInfoDictionary(pdfDoc);
  if (!dictionary) return;
  entries
    .filter((entry) => entry.source === 'Info' && entry.editable)
    .forEach((entry) => {
      if (entry.value === '') dictionary.delete(PDFName.of(entry.key));
      else dictionary.set(PDFName.of(entry.key), PDFHexString.fromText(entry.value));
    });
};

const setContainerValue = (element, value, containerName) => {
  if (!element) return;
  const container = getElement(element, NS.rdf, containerName);
  if (!container) {
    element.textContent = value;
    return;
  }
  const items = Array.from(container.getElementsByTagNameNS(NS.rdf, 'li'));
  if (items.length) items[0].textContent = value;
};

const updateKeywordBag = (doc, element, value) => {
  if (!element) return;
  const bag = getElement(element, NS.rdf, 'Bag');
  if (!bag) {
    element.textContent = value;
    return;
  }
  while (bag.firstChild) bag.removeChild(bag.firstChild);
  splitKeywords(value).forEach((keyword) => {
    const item = doc.createElementNS(NS.rdf, 'rdf:li');
    item.textContent = keyword;
    bag.appendChild(item);
  });
};

const updateXmp = (xml, metadata) => {
  if (!xml) return '';
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  if (doc.getElementsByTagName('parsererror').length) return '';

  setContainerValue(getElement(doc, NS.dc, 'title'), metadata.title, 'Alt');
  setContainerValue(getElement(doc, NS.dc, 'creator'), metadata.author, 'Seq');
  setContainerValue(getElement(doc, NS.dc, 'description'), metadata.subject, 'Alt');
  updateKeywordBag(doc, getElement(doc, NS.dc, 'subject'), metadata.keywords);

  const directValues = [
    [NS.pdf, 'Keywords', metadata.keywords],
    [NS.pdf, 'Producer', metadata.producer],
    [NS.xmp, 'CreatorTool', metadata.creator],
    [NS.xmp, 'CreateDate', toPdfDate(metadata.created)?.toISOString() || ''],
    [NS.xmp, 'ModifyDate', toPdfDate(metadata.modified)?.toISOString() || ''],
  ];
  directValues.forEach(([namespace, name, value]) => {
    const element = getElement(doc, namespace, name);
    if (element) element.textContent = value;
  });

  return new XMLSerializer().serializeToString(doc);
};

export async function writePdfMetadata(source, metadata) {
  let pdfDoc;
  try {
    pdfDoc = await PDFDocument.load(source.bytes, { updateMetadata: false });
  } catch (error) {
    throw friendlyPdfError(error);
  }

  setTextMetadata(pdfDoc, 'Title', metadata.title);
  setTextMetadata(pdfDoc, 'Author', metadata.author);
  setTextMetadata(pdfDoc, 'Subject', metadata.subject);
  setTextMetadata(pdfDoc, 'Creator', metadata.creator);
  setTextMetadata(pdfDoc, 'Producer', metadata.producer);
  if (metadata.keywords) pdfDoc.setKeywords(splitKeywords(metadata.keywords));

  const created = toPdfDate(metadata.created);
  const modified = toPdfDate(metadata.modified);
  if (created) pdfDoc.setCreationDate(created);
  if (modified) pdfDoc.setModificationDate(modified);

  deleteEmptyStandardValues(pdfDoc, metadata);
  updateCustomInfo(pdfDoc, metadata.customInfo || []);

  const updatedXmp = updateXmp(source.xmpXml, metadata);
  if (updatedXmp) {
    const metadataStream = pdfDoc.context.stream(updatedXmp, {
      Type: 'Metadata',
      Subtype: 'XML',
    });
    const metadataRef = pdfDoc.context.register(metadataStream);
    pdfDoc.catalog.set(PDFName.of('Metadata'), metadataRef);
  }

  return pdfDoc.save();
}
